import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Textbaustein, TextbausteinFelder,
              Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, Objektliste, Objektzeile,
         useBeforeUnload, useFormularZustand, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../../router.js";
import { Meldung, ersteZeile, meldungAus } from "./gemeinsam.js";
import { ohneNummerierungsKopf } from "../../markdown.js";
import { MarkdownHinweis } from "../../components/MarkdownHinweis.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich TEXTBAUSTEINE (textBlock, Paket 3b-1a) — wiederverwendbare
 * Textstücke mit Platzhaltern für die Einleitungs- und Ausleitungstexte an Angebot und
 * Auftrag.
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Die Bausteinliste steht LINKS im Inhalt
 * (die des aktiven Unternehmens, E364), rechts daneben der gewählte Baustein — und der zeigt
 * seinen Text seit Etappe 2 GANZ, statt nur die erste Zeile wie in der früheren
 * Kartenliste. Zwischenzeitlich stand die Liste als zweite Ebene in der Verwaltungs-
 * Spalte; dort war sie neben dem Bereichs-Untermenü redundant und zu schmal. Die
 * Spezifikation nennt die Textbausteine ausdrücklich als
 * Objekt-Bereich.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * Zwei Fachregeln bleiben unverändert:
 *
 *  - **Firmenübergreifend ist ein eigener Zustand.** Ein Baustein ohne Unternehmen gilt für
 *    alle. Die Liste des aktiven Unternehmens ist deshalb KEINE Einschränkung auf eine Firma
 *    allein: Sie liefert deren Bausteine UND die gemeinsamen. Die Liste sagt das an jeder Zeile (Marke)
 *    und die Liste als Satz darüber — sonst wirkte es wie ein Fehler, dass fremde Zeilen
 *    stehenbleiben.
 *  - **Löschen statt Stilllegen.** Ein Baustein trägt kein `active`; er verschwindet ganz —
 *    deshalb die Rückfrage vor dem Löschen. Gefahrlos, weil ein benutzter Baustein längst
 *    als Text im Vorgang steht.
 *
 * Der Text wird als ROHTEXT mit seinen `#{...}`-Tokens bearbeitet und gespeichert; hier
 * wird NICHTS aufgelöst (Ruling P3B-ENGINE — die verbindliche Auflösung fürs PDF macht die
 * geteilte Render-Engine in 3b-2). Auch die Anzeige im Detail ist roh: Was dort steht, ist
 * genau das, was gespeichert ist. Die Legende unter dem Editor ist reine Schreibhilfe und
 * kommt VOM SERVER: `web` darf `domain` nicht importieren, und eine hier abgetippte Liste
 * wäre eine zweite Wahrheit, die beim nächsten neuen Platzhalter still veraltet.
 *
 * **CSS:** Alles hier trägt den Block `vw__` (views/Verwaltung.css) — Liste und
 * Detail stehen im Inhalt; der Spaltenblock `vws__` gehört allein dem Bereichs-Untermenü.
 */

/** Nur die vier Wege dieses Bereichs — so lässt er sich im Test mit vier vi.fn zeichnen. */
export type TextbausteineClient = Pick<ApiClient,
  "listTextBlocks" | "createTextBlock" | "updateTextBlock" | "deleteTextBlock">;

type TextbausteinDialog = {
  /** null = neuer Baustein. */
  id: string | null;
  /** Leer = firmenübergreifend (der Server bekommt dafür ein ausdrückliches `null`). */
  companyId: string;
  name: string;
  body: string;
};

type TextbausteinFeld = "companyId" | "name" | "body";

const TEXTBAUSTEIN_FELDNAMEN: readonly TextbausteinFeld[] = ["companyId", "name", "body"];

function textbausteindialogAus(t: Textbaustein | null): TextbausteinDialog {
  if (t === null) return { id: null, companyId: "", name: "", body: "" };
  return { id: t.id, companyId: t.companyId ?? "", name: t.name, body: t.body };
}

function textbausteindialogGleich(a: TextbausteinDialog, b: TextbausteinDialog): boolean {
  return a.id === b.id && a.companyId === b.companyId && a.name === b.name
    && a.body === b.body;
}

/** „in 2 Vorlagen, 1 Entwurf" — oder nichts, wenn der Baustein frei ist (Migration 031).
 *  Eine Zeile „in 0 Vorlagen, 0 Entwürfen" wäre Lärm an jeder Zeile, die den Normalfall
 *  zeigt. */
function verwendungsText(v: { vorlagen: number; entwuerfe: number }): string | null {
  const teile: string[] = [];
  if (v.vorlagen > 0) {
    teile.push(`${String(v.vorlagen)} ${v.vorlagen === 1 ? "Vorlage" : "Vorlagen"}`);
  }
  if (v.entwuerfe > 0) {
    teile.push(`${String(v.entwuerfe)} ${v.entwuerfe === 1 ? "Entwurf" : "Entwürfe"}`);
  }
  return teile.length === 0 ? null : `in ${teile.join(", ")}`;
}

/** Ist der Baustein gebunden? Dann sperrt die Liste den Löschknopf — der Server tut es
 *  ohnehin (409 TEXTBAUSTEIN_IN_VERWENDUNG), aber ein Knopf, der es VORHER sagt, ist
 *  besser als eine Meldung danach. Die 409 bleibt trotzdem der Riegel für den Wettlauf
 *  zwischen dem Laden dieser Liste und dem Klick — siehe `loeschen`. */
function gebunden(t: Textbaustein): boolean {
  return t.verwendung.vorlagen + t.verwendung.entwuerfe > 0;
}

/** Wie bei den Vorreitern der Aufgabe 2 — siehe dort die ausführliche Begründung. */
export type VerwaltungTextbausteineProps = {
  aktiv: boolean;
  client: TextbausteineClient;
  companies: Unternehmen[];
  /** Der geöffnete Baustein aus der Adresse (`/verwaltung/textbausteine/:id`). */
  textbausteinId: string | null;
  onOeffnen: (textbausteinId: string | null) => void;
  /** Das aktive Unternehmen des Fensters — Liste und Vorgabe der Neuanlage folgen ihm (E364). */
  arbeitsunternehmenId: string | null;
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export function useVerwaltungTextbausteine(
  p: VerwaltungTextbausteineProps,
): VerwaltungBereichTeile {
  const { aktiv, textbausteinId } = p;

  const [zeilen, setZeilen] = useState<Textbaustein[] | null>(null);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  // Die Textbausteine folgen dem aktiven Unternehmen (Spec Mandantentrennung, E364) — keine eigene Auswahl mehr.
  const companyId = p.arbeitsunternehmenId ?? "";
  const [dialog, setDialog] = useState<TextbausteinDialog | null>(null);
  /** Der Ausgangsstand des ANLEGE-Dialogs, so wie er geöffnet wurde — samt Vorbelegung mit
   *  dem aktiven Unternehmen. Er ist die Vergleichsbasis für „ist etwas
   *  ungespeichert?": Gegen den LEEREN Dialog verglichen, zählte die Vorbelegung allein als
   *  Eingabe, und Escape/Abbrechen fragte ohne jede Eingabe nach (Fix F1, E2E 08.10.2026).
   *  Festgehalten statt neu gerechnet: Er ist der Stand beim Öffnen. */
  const [anlageAusgang, setAnlageAusgang] = useState<TextbausteinDialog | null>(null);
  /** Fehlschlag des Löschens am Detail (kein Formular). */
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Der Baustein, der gerade gelöscht wird (Knopf sperren). */
  const [loescht, setLoescht] = useState<string | null>(null);
  /** Wird hochgezählt, wenn eine Schreibaktion die Liste neu holen soll. */
  const [neuLaden, setNeuLaden] = useState(0);

  const listTextBlocks = p.client.listTextBlocks;

  useEffect(() => {
    if (!aktiv) return;
    // Ohne aktives Unternehmen ist die Sicht leer (Spec Mandantentrennung R6, R14) — nie „alle“.
    if (companyId === "") { setZeilen([]); setLadeFehler(null); return; }
    let abgebrochen = false;
    listTextBlocks({ companyId })
      .then((antwort) => {
        if (abgebrochen) return;
        setZeilen(antwort.items);
        setPlatzhalter(antwort.platzhalter);
        // Ein geglücktes Nachladen HEILT den alten Fehler (Lehre der Fixrunde 1 zu
        // Aufgabe 2): Der Hook lebt über der ganzen Anwendung, sein Zustand überlebt jeden
        // Bereichswechsel — der Fehlerstreifen stünde sonst über einer Liste, die längst
        // wieder da ist.
        setLadeFehler(null);
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Die Textbausteine konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listTextBlocks, companyId, neuLaden]);

  /**
   * Formularzustände der Maske (ME-Etappe 3, Aufgabe 6): `laeuft` sperrt Felder (auch das
   * Textfeld) und Fuß, eine 422 hängt am Feld und der Fokus springt dorthin. Ein Baustein
   * trägt keine Version — eine 409 gibt es beim Speichern nicht.
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Kein Textbaustein offen.");
      let angelegtId: string | null = null;
      if (d.id === null) {
        angelegtId = (await p.client.createTextBlock(feldsatzAus(d))).id;
      } else {
        await p.client.updateTextBlock(d.id, feldsatzAus(d));
      }
      // In beiden Fällen neu lesen: Ein geänderter Name verschiebt die Zeile innerhalb der
      // Ordnung, und eine geänderte Zugehörigkeit kann sie aus der Liste des aktiven
      // Unternehmens herausfallen lassen. Beides weiss nur der Server.
      setNeuLaden((n) => n + 1);
      setDialog(null);
      // Nach dem ANLEGEN geht die Adresse auf den frischen Satz (Gegenstück zum
      // `onOeffnen(null)` im Löschen): Wer eben etwas angelegt hat, will es sehen — und die
      // Kennung, die das Anlegen zurückgibt, ist genau dafür da. Beim Ändern bleibt die
      // Adresse, wo sie ist; sie zeigt ja bereits auf diesen Satz.
      if (angelegtId !== null) p.onOeffnen(angelegtId);
      return true;
    },
    neuLaden: async () => { setNeuLaden((n) => n + 1); },
    bekannteFelder: TEXTBAUSTEIN_FELDNAMEN,
    beschriftungen: { companyId: "Gilt für", name: "Name", body: "Text" },
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<TextbausteinFeld, string>>;

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion): der halb
   * ausgefüllte Dialog, Feld- und Sammelmeldungen, der Ladefehler. Der FILTER bleibt: Er
   * ist keine angefangene Eingabe, sondern die Sicht auf die Liste — dieselbe Regel wie
   * beim Suchtext der Kundenliste, der einen Bereichswechsel ebenfalls überlebt.
   */
  useEffect(() => {
    if (aktiv) return;
    setDialog(null);
    formular.zuruecksetzen();
    setMeldung(null);
    setLadeFehler(null);
  }, [aktiv]);

  /* Der Änderungsdialog gehört DEM Baustein, über dem er geöffnet wurde — er fällt mit der
     ADRESSE, nicht mit dem Klick, der ihn sonst allein geschlossen hätte (Zurück-Taste,
     Rückweg im Kopf, ein Lesezeichen auf einen anderen Baustein). Der ANLEGE-Dialog
     (`id === null`) bleibt: Er gehört keinem Objekt, sondern dem Bereich, und steht
     bei der Liste. */
  useEffect(() => {
    setDialog((d) => (d === null || d.id === null || d.id === textbausteinId ? d : null));
    // Und die Sammelmeldung mit (Fixrunde 1): Sie gehört dem Objekt, über dem sie entstand
    // — ein gescheiterter Löschversuch am einen Baustein darf nicht als Alarm über dem
    // nächsten stehenbleiben. Dieselbe Regel wie beim `hinweis` in BereichArtikel.
    setMeldung(null);
    formular.zuruecksetzen();
  }, [textbausteinId]);

  const dialogGeaendert = dialog !== null && !textbausteindialogGleich(dialog,
    dialog.id === null
      ? (anlageAusgang ?? textbausteindialogAus(null))
      : textbausteindialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));
  useBeforeUnload(dialogGeaendert);

  /** OB gerade bearbeitet wird — ein offener Dialog dieses Bereichs mit Ungespeichertem.
   *  Die Spaltenliste ist währenddessen gesperrt (Regel aus Etappe 1). */
  const bearbeitung = aktiv && dialogGeaendert;

  const { bestaetigen } = useBestaetigung();

  /** Der Verlassen-Wächter des ROUTERS (Hausmuster). Bestätigtes Verlassen VERWIRFT den
   *  Dialog — sonst sagte die Rückfrage die Unwahrheit (Lehre der Fixrunde 1 zu Aufgabe 2).
   *  NUR für den ÄNDERUNGSDIALOG: Der Anlege-Dialog schliesst und ruft im selben Zug
   *  `onOeffnen` für den frischen Satz; ein noch angemeldeter Wächter fragte den Router vor
   *  dem Sprung (derselbe Befund wie bei den Angebotsvorlagen, E2E-Torlauf 24.09.2026). */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  const aenderungsDialogOffen = dialog !== null && dialog.id !== null;
  useEffect(() => {
    if (!bearbeitung || !aenderungsDialogOffen || registriereVerlassenWaechter === undefined) {
      return;
    }
    return registriereVerlassenWaechter(async () => {
      const weiter = await bestaetigen({
        titel: "Seite verlassen?",
        text: "Dieser Textbaustein hat ungespeicherte Änderungen. Sie gehen verloren.",
        bestaetigenText: "Verlassen",
        gefaehrlich: true,
      });
      if (weiter) { setDialog(null); formular.zuruecksetzen(); setMeldung(null); }
      return weiter;
    });
  }, [bearbeitung, aenderungsDialogOffen, registriereVerlassenWaechter]);

  function firmenName(id: string | null): string {
    if (id === null) return "firmenübergreifend";
    return p.companies.find((c) => c.id === id)?.shortCode ?? "fremdes Unternehmen";
  }

  function oeffnen(t: Textbaustein | null): void {
    formular.zuruecksetzen();
    setMeldung(null);
    const entwurf = textbausteindialogAus(t);
    // Beim Anlegen mit dem aktiven Unternehmen vorbelegen — wer die Bausteine einer Firma
    // ansieht, schreibt fast immer für dieselbe. Ohne aktives bleibt es „für alle".
    const offen = t === null ? { ...entwurf, companyId } : entwurf;
    setDialog(offen);
    setAnlageAusgang(t === null ? offen : null);
  }

  function feldsatzAus(d: TextbausteinDialog): TextbausteinFelder {
    // Die Fachprüfung (Pflichtname, Pflichttext, Längen) macht der Server. Hier steht nur
    // die eine Umrechnung, die die Maske selbst kennt: leere Auswahl = firmenübergreifend.
    return {
      companyId: d.companyId === "" ? null : d.companyId,
      name: d.name,
      body: d.body,
    };
  }

  async function speichern(): Promise<void> {
    await formular.aktion();
  }

  async function loeschen(t: Textbaustein): Promise<void> {
    // Rückfrage, weil es kein Zurück gibt: Ein Baustein wird nicht stillgelegt, sondern
    // gelöscht.
    if (!(await bestaetigen({
      titel: "Textbaustein löschen?",
      text: `„${t.name}" wirklich löschen? Das lässt sich nicht rückgängig machen.`,
      bestaetigenText: "Löschen",
      gefaehrlich: true,
    }))) {
      return;
    }
    setMeldung(null);
    setLoescht(t.id);
    try {
      await p.client.deleteTextBlock(t.id);
      setZeilen((alt) => (alt === null ? alt : alt.filter((x) => x.id !== t.id)));
      // Und die Adresse mit: Was gelöscht ist, steht in der Liste nicht mehr; das Detail
      // zeigte sonst „gibt es nicht (mehr)" über einer Handlung, die gerade geglückt ist.
      p.onOeffnen(null);
    } catch (fehler) {
      // 409 TEXTBAUSTEIN_IN_VERWENDUNG: Die Liste sperrt den Knopf schon (siehe
      // `gebunden`), aber zwischen dem Laden und dem Klick kann jemand den Baustein
      // angehängt haben. Die Meldung des Servers nennt die Zähler und ist damit die
      // genauere — sie wird unverändert durchgereicht (`meldungAus` liest `fehler.message`
      // an jeder ApiError, ganz gleich welchen Codes).
      setMeldung(meldungAus(fehler, "Der Textbaustein konnte nicht gelöscht werden."));
    } finally {
      setLoescht(null);
    }
  }

  /** Der eine Weg aus dem Modal neben dem Speichern (Hausregel „striktes Modalpanel",
   *  06.09.2026) — mit Rückfrage, wenn etwas Ungespeichertes darin steht. Escape ruft
   *  denselben Weg (siehe `Modal` in @kundeninfo/ui); ein Klick daneben schliesst nicht. */
  async function abbrechen(): Promise<void> {
    // Kein Ausgang, solange geschrieben wird — das gilt für die ESCAPE-Taste genauso wie
    // für den (dann gesperrten) Knopf: Beide gehen durch diese Funktion, und die Antwort
    // des Servers braucht ihr Panel noch (Fixrunde 1).
    if (arbeitet) return;
    if (!(await darfAbbrechen(dialogGeaendert))) return;
    setDialog(null);
  }

  /** Der Dialograhmen — für Anlegen (bei der Liste) und Ändern (im Detail) derselbe. */
  function dialogRahmen(d: TextbausteinDialog): ReactElement {
    return (
      <Modal titel={d.id === null ? "Neuer Textbaustein" : "Textbaustein ändern"}
        onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
        {formular.meldung !== null && <Meldung text={formular.meldung} />}
        <Formular beschriftung="oben" {...formular.formularProps}>
          <Auswahl id="vw-t-unternehmen" name="vw-t-unternehmen" beschriftung="Gilt für"
            wert={d.companyId}
            {...(feldFehler.companyId !== undefined ? { fehler: feldFehler.companyId } : {})}
            onGeaendert={(e) => setDialog((alt) => (alt === null ? alt
              : { ...alt, companyId: e.wert }))}>
            {/* Auch beim Ändern wählbar: Am Unternehmen eines Bausteins hängt kein
                Nummernraum (anders als beim Artikel) — ihn für alle freizugeben ist eine
                gewöhnliche Pflegehandlung. Zur Wahl stehen seit der Mandantentrennung (E364)
                nur das aktive Unternehmen und „alle"; eine abweichende bestehende Zuordnung
                bleibt als Option stehen, damit sie nicht still umspringt. */}
            {p.companies.filter((c) => c.id === companyId
              || (c.id === d.companyId && d.companyId !== "")).map((c) => (
              <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
            ))}
            <option value="">Alle Unternehmen</option>
          </Auswahl>
          <Feld id="vw-t-name" label="Name" bearbeitbar wert={d.name}
            onChange={(w) => setDialog((alt) => (alt === null ? alt : { ...alt, name: w }))}
            {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
          {/* `Feld typ="mehrzeilig"` (me-eingabe, ME-Etappe 4 P6): Der Anfangswert behält seine
              Zeilenumbrüche (me-design c9c0c08, Befund 2); Befund und Sperre bringt das Element.
              Die Legende der Platzhalter liegt ausserhalb des Feldes (ein Verweis per
              aria-describedby ginge aus dem Shadow DOM verloren) — ihr Satz steht deshalb als
              `hilfe` am Feld, die Liste selbst darunter als Gruppe „Bekannte Platzhalter“. */}
          <div className="vw__auswahl">
            <Feld id="vw-t-body" label="Text" typ="mehrzeilig" zeilen={8} bearbeitbar
              wert={d.body}
              hilfe="Platzhalter bleiben als Text stehen und werden erst beim Drucken ersetzt."
              {...(feldFehler.body !== undefined ? { fehler: feldFehler.body } : {})}
              onChange={(w) => setDialog((alt) => (alt === null ? alt
                : { ...alt, body: w }))} />
            {/* Ein Baustein landet als Einleitungs- oder Ausleitungstext im Beleg — also
                gilt für ihn dieselbe Auszeichnungssprache wie dort. */}
            <MarkdownHinweis />
          </div>
        </Formular>
        {/* Die Legende: was hier stehen darf. Sie kommt mit der Liste vom Server (siehe
            Kopf) und wird NICHT aufgelöst — im Text bleibt stehen, was getippt wurde. */}
        <div className="vw__vorschau" role="group"
          aria-label="Bekannte Platzhalter">
          <span className="vw__eintrag-zusatz">Bekannte Platzhalter:</span>
          <div className="vw__marken">
            {platzhalter.map((name) => (
              <Chip key={name} className="vw__marke"><code>{`#{${name}}`}</code></Chip>
            ))}
          </div>
        </div>
        <ModalFuss speichernText="Speichern"
          speichernGesperrt={formular.zustand !== "bereit"}
          abbrechenGesperrt={arbeitet}
          onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
      </Modal>
    );
  }

  const satz = textbausteinId === null
    ? null
    : (zeilen ?? []).find((t) => t.id === textbausteinId) ?? null;

  // ---------------------------------------------------------------------------
  // Die Objektliste — zweite Ebene unter dem Eintrag „Textbausteine"
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      <div className="vw__leiste">
        <KiKnopf art="primaer" deaktiviert={bearbeitung} onClick={() => oeffnen(null)}>
          Neuer Textbaustein
        </KiKnopf>
      </div>

      <p className="vw__hinweis">
        Ein Baustein ohne Unternehmen gilt firmenübergreifend — er steht bei jedem
        Unternehmen mit in der Liste.
      </p>

      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && zeilen !== null && zeilen.length === 0 && (
        <p className="vw__hinweis">Für dieses Unternehmen gibt es noch keinen Textbaustein.</p>
      )}

      <Objektliste label="Textbausteine">
        {(zeilen ?? []).map((t) => (
          <Objektzeile
            key={t.id}
            name={t.name}
            /* Die erste Zeile des Bausteins als Vorschau — mehr passt nicht in eine Zeile,
               der ganze Text steht im Editor (`ersteZeile`, siehe gemeinsam.tsx). */
            meta={ersteZeile(ohneNummerierungsKopf(t.body))}
            marken={[firmenName(t.companyId),
                     ...(verwendungsText(t.verwendung) === null
                       ? [] : [verwendungsText(t.verwendung)!])]}
            gewaehlt={t.id === textbausteinId}
            gesperrt={bearbeitung}
            ariaLabel={`Textbaustein ${t.name}, ${firmenName(t.companyId)}, anzeigen`}
            onOeffnen={() => p.onOeffnen(t.id)}
          />
        ))}
      </Objektliste>

      {/* Der Anlege-Dialog steht bei seinem Knopf, also bei der Liste. */}
      {dialog !== null && dialog.id === null && dialogRahmen(dialog)}
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — GENAU EIN Baustein
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (textbausteinId === null) {
      return (
        <p className="vw__hinweis">
          Links einen Textbaustein wählen — sein Text steht dann hier in voller Länge, mit
          seinen Platzhaltern, so wie er gespeichert ist.
        </p>
      );
    }
    if (satz === null) {
      // Die dreistufige Auskunft für ein Lesezeichen auf einen Baustein, den die Liste
      // nicht enthält (Etappe-1-Lektion) — er kann auch einem anderen Unternehmen
      // gehören (die Liste zeigt nur das aktive, E364), deshalb nennt der letzte Satz das.
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <p className="vw__hinweis">
          Diesen Textbaustein gibt es nicht (mehr) — oder er gehört zu einem anderen als dem
          aktiven Unternehmen. Bitte links einen wählen.
        </p>
      );
    }

    const t = satz;
    return (
      /* Eigene Identität je Baustein (Etappe-1-Lektion `key={objekt.id}`). */
      <div key={t.id} className="vw__eintrag">
        {meldung !== null && dialog === null && <Meldung text={meldung} />}
        <div className="vw__eintrag-kopf">
          <span className="vw__eintrag-name">{t.name}</span>
          <Chip className="vw__marke">{firmenName(t.companyId)}</Chip>
        </div>
        {/* Der Text ROH — mit seinen Umbrüchen und seinen `#{...}`-Tokens. Aufgelöst wird
            er erst beim Drucken (Ruling P3B-ENGINE). */}
        <p className="vw__rohtext">{t.body}</p>
        <div className="vw__eintrag-aktionen">
          <KiKnopf name={`${t.name} ändern`} onClick={() => oeffnen(t)}>
            Ändern
          </KiKnopf>
          <LoeschKnopf deaktiviert={loescht === t.id || gebunden(t)} name={`${t.name} löschen`}
            onClick={() => { void loeschen(t); }} />
        </div>
        {/* Migration 031: Der Grund, warum der Löschknopf gesperrt ist — mit den Zählern,
            nicht bloss „geht nicht". */}
        {gebunden(t) && (
          <span className="vw__eintrag-zusatz">
            {`Als Anlage in Verwendung (${verwendungsText(t.verwendung) ?? ""}) — `}
            {"er wird frei, sobald die betroffenen Angebote gesendet sind (dann tragen "}
            {"sie einen Schnappschuss seines Textes)."}
          </span>
        )}

        {/* Der Änderungsdialog steht bei seinem Knopf, also im Detail — und gehört GENAU
            DEM Baustein, über dem er geöffnet wurde (Lehre der Fixrunde 1 zu Aufgabe 2). */}
        {dialog !== null && dialog.id === t.id && dialogRahmen(dialog)}
      </div>
    );
  })();

  return { liste, detail: detail };
}
